import { useEffect, useState, useRef } from 'react'
import moment from 'moment'
import 'moment/locale/ko'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'

// components
import Footer from './components/footer/Footer'
import { FooterMenu, InfoMenu, MainMenu } from './components/sidebar'
import { ScrollButton } from './components'

// router
import Router from './routers/Router'
import AuthRouter from './routers/AuthRouter'

// global
import * as constants from './globals/constants'
import * as TYPE from './globals/type'
import StoreContext from './globals/StoreContext'

// utils
import { getLastPath } from './utils/getLastPath'
import { useAxios } from './utils/useAxios'
import { useUser } from './utils/useUser'
import { useHappyOrder } from './utils/useHappyOrder'
import { useNewNotice } from './utils/useNewNotice'

// style
import GlobalStyles from './styles/GlobalStyles'
import theme from './styles/theme'
import colors from './styles/colors'

// images
import OpenImg from './assets/image/sidebar/ic-menu-open.svg'
import CloseImg from './assets/image/sidebar/ic-menu-close.svg'
import { useNavigate } from 'react-router-dom'

function App() {
  const user = useUser()
  const happyOrder = useHappyOrder()
  const newNotice = useNewNotice()

  const { fetchData, loading, data, error } = useAxios()
  const [storeInfoData, setStoreInfoData] = useState('')
  const [curPage, setCurPage] = useState('')
  const [brandInfo, setBrandInfo] = useState({
    brandName: '', // 브랜드명
    storeName: '', // 매장명
    isOpen: false, // 영업 상태
  })
  const [menuInfo, setMenuInfo] = useState({
    logo: null, // 로고 정보
    isSettlement: false, // 정산내역 표시
  })
  const [isAgree, setIsAgree] = useState(false)
  const [noticeAlert, setNoticeAlert] = useState(false)
  const closeRef = useRef(null)

  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [hiddenYn, setHiddenYn] = useState() //브랜드별 메뉴 숨기기

  // 약관
  const isTerm =
    getLastPath(window.location.pathname).includes(
      constants.CURRENT_URL.SERVICE_TERMS,
    ) ||
    getLastPath(window.location.pathname).includes(
      constants.CURRENT_URL.PRIVACY_TERMS,
    )

  // 매장 정보(배달, 픽업) 조회
  const userData = JSON.parse(localStorage.getItem('user'))
  const params = {
    manageType: userData?.manageType ?? 9, //manageType 예외 처리.  manageTyper값이 hiddenYn구분함
  }
  const getStoreInfo = () => {
    fetchData(constants.API.STORES_INFO, 'GET', null, params)
  }

  useEffect(() => {
    let pageName = TYPE.PAGE_NAME.ETC

    if (getLastPath(pathname).includes(TYPE.PAGE_URL.ORDER_LIST)) {
      pageName = TYPE.PAGE_NAME.ORDER
    } else if (pathname.includes(TYPE.PAGE_URL.NOTICE_LIST)) {
      pageName = TYPE.PAGE_NAME.NOTICE
    } else {
      switch (getLastPath(pathname)) {
        // 메뉴관리
        case TYPE.PAGE_URL.ITEM_LIST:
          pageName = TYPE.PAGE_NAME.ITEM
          break

        // 옵션관리
        case TYPE.PAGE_URL.OPTION_LIST:
          pageName = TYPE.PAGE_NAME.ITEM
          break

        // 매장관리
        case TYPE.PAGE_URL.STORE_LIST:
          pageName = TYPE.PAGE_NAME.STORE
          break
        case TYPE.PAGE_URL.MANAGE_LIST:
          pageName = TYPE.PAGE_NAME.STORE
          break
        case TYPE.PAGE_URL.DELIVERY_LIST:
          pageName = TYPE.PAGE_NAME.STORE
          break

        // 정산내역
        case TYPE.PAGE_URL.SETTLEMENT:
          pageName = TYPE.PAGE_NAME.SETTLEMENT
          break

        // 부가세 신고자료
        case TYPE.PAGE_URL.VAT_REPORT:
          pageName = TYPE.PAGE_NAME.VAT_REPORT
          break

        // 통계
        case TYPE.PAGE_URL.STAT:
          pageName = TYPE.PAGE_NAME.STAT
          break

        // 리뷰관리
        case TYPE.PAGE_URL.REVIEW_LIST:
          pageName = TYPE.PAGE_NAME.REVIEW
          break

        // 리뷰관리
        case TYPE.PAGE_URL.GUIDE:
          pageName = TYPE.PAGE_NAME.GUIDE
          break

        // 로그인
        case TYPE.PAGE_URL.SIGN_IN:
        case TYPE.PAGE_URL.FIND_ID:
        case TYPE.PAGE_URL.RESET_PASSWORD:
        case TYPE.PAGE_URL.CHANGE_PASSWORD:
          pageName = TYPE.PAGE_NAME.SIGN_IN
          break

        default:
          break
      }
    }

    setCurPage(pageName)
  }, [pathname])

  useEffect(() => {
    if (window.location.pathname.includes('error')) {
      navigate('/error', { replace: true })
      return
    }
    // 유저 정보 X
    if (window.location.pathname.includes('agentPOS')) {
      // if (user) {
      navigate('/agentPOS', { replace: true })
      // }
    }
    if (!window.location.pathname.includes('agentPOS')) {
      if (!user) {
        // 로그인으로 이동
        navigate('/signin', { replace: true })
      }
      // 유저 정보 O
      else {
        const userData = JSON.parse(localStorage.getItem('user'))

        // 동의 여부 체크
        setIsAgree(userData.agreementTermsYn === 'Y')
        if (userData.agreementTermsYn == 'N') {
          navigate('/useguide')
        }
        // 홈으로 이동
        if (getLastPath(window.location.pathname).includes('signin') && user) {
          navigate('/', { replace: true })
        }
      }
    }
  }, [user])

  // 매장상태 변경 되면 사이드바 영업중/영업준비중 값 변경하기 위해 매장 정보 호출
  useEffect(() => {
    // 로그인 성공 한 경우에만 사이드바 관련 정보 가져옴
    if (user) {
      getStoreInfo()
    }
  }, [])

  // 공지사항 변경
  useEffect(() => {
    setNoticeAlert(localStorage.getItem('isNewNotice') === 'true')
  }, [newNotice])

  useEffect(() => {
    let resCode = data?.responseCode
    let resData = data?.responseData
    switch (resCode) {
      // 매장 정보 조회 성공
      case TYPE.API_RESPONSE.SUCCESS:
        if (resData) {
          setBrandInfo({
            brandName: resData.storeInfo.brandName,
            storeName: resData.storeInfo.storeName,
            isOpen: checkOpen(resData.storeInfo), // 변경(휴무일, 영업시간, 해피오더 모두 고려)
          })

          const userData = JSON.parse(localStorage.getItem('user'))

          setMenuInfo({
            logo: userData
              ? `${constants.IMG_URL}${userData.brandImg}?version=${constants.BRAND_IMG_VERSION}`
              : null,
            isSettlement: resData?.hiddenMenuEnableYn === 'Y',
          })
          setHiddenYn(resData?.hiddenMenuEnableYn === 'Y')

          setStoreInfoData(data)

          break
        }
      // 실패
      case TYPE.API_RESPONSE.FAILURE:
      default:
        break
    }
  }, [data])

  const checkOpen = (storeInfo) => {
    if (!storeInfo) return

    const {
      closedDay, // 휴무일
      happyorderYn, // 해피오더
      shopHoursStart, // 운영시간 시작
      shopHoursEnd, // 운영시간 마감
    } = storeInfo

    const currentDay = moment().format('YYYYMMDD') // 오늘
    const currentTime = moment().format('HHmm') // 현재 시간

    // 영업시간 시작
    const shopHoursStartTime = moment({
      hour:
        shopHoursStart.length === 4
          ? shopHoursStart.slice(0, 2)
          : shopHoursStart.slice(0, 1),
      minute:
        shopHoursStart.length === 4
          ? shopHoursStart.slice(2, 4)
          : shopHoursStart.slice(1, 4),
    }).format('HHmm') // 영업 시작 시간

    // 영업시간 마감
    const shopHoursEndTime = moment({
      hour:
        shopHoursEnd.length === 4
          ? shopHoursEnd.slice(0, 2)
          : shopHoursEnd.slice(0, 1),
      minute:
        shopHoursEnd.length === 4
          ? shopHoursEnd.slice(2, 4)
          : shopHoursEnd.slice(1, 4),
    }).format('HHmm') // 영업 시작 시간

    if (closedDay?.includes(currentDay)) {
      return false
    }

    // 휴무일 아니고
    else {
      // 해피오더 켜짐 or 영업시간 내
      if (
        happyorderYn === 'Y' ||
        (shopHoursStartTime <= currentTime && shopHoursEndTime >= currentTime)
      ) {
        return true
      } else {
        return false
      }
    }
  }

  const hideSidebar = () => {
    if (closeRef?.current && window.innerWidth <= 1024) {
      closeRef.current.click()
    }
  }

  const onClickSignOut = () => {
    fetchData(constants.API.SIGN_OUT, 'POST')
  }

  return (
    <>
      {!user ? (
        <ThemeProvider theme={theme}>
          <AuthRouter />
        </ThemeProvider>
      ) : (
        <>
          <GlobalStyles />
          <StoreContext.Provider value={{ storeInfoData, getStoreInfo }}>
            <ThemeProvider theme={theme}>
              <div className="row min-vh-100 g-0">
                {/* 사이드바 PC */}

                {isAgree && !isTerm && (
                  <SideBarWrapper>
                    <InfoMenu brandInfo={brandInfo} menuInfo={menuInfo} />
                    <MainMenu
                      hideSidebar={hideSidebar}
                      isSettlement={menuInfo.isSettlement}
                      isNewNotice={noticeAlert}
                    />
                    <FooterMenu
                      hiddenYn={hiddenYn}
                      hideSidebar={hideSidebar}
                      onClickSignOut={onClickSignOut}
                    />
                  </SideBarWrapper>
                )}

                {/* 컨텐츠 */}
                <ContentsWrapper>
                  <HeaderWrapperMo>
                    {isAgree && !isTerm && (
                      <OpenSidebar>
                        <button
                          type="button"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasWithBothOptions"
                          aria-controls="offcanvasWithBothOptions"
                        />
                      </OpenSidebar>
                    )}

                    {!isTerm && (
                      <CurrentPageHeader>{curPage}</CurrentPageHeader>
                    )}
                  </HeaderWrapperMo>
                  <ScrollButton />

                  <Router hiddenYn={hiddenYn} />

                  <Footer isTerm={isTerm} />
                </ContentsWrapper>

                {/* 사이드바 모바일 */}
                <div
                  className="offcanvas offcanvas-start"
                  data-bs-scroll="false"
                  tabIndex="-1"
                  id="offcanvasWithBothOptions"
                  aria-labelledby="offcanvasWithBothOptionsLabel"
                  style={{ padding: '0px', backgroundColor: colors.W1 }}
                >
                  <div className="offcanvas-body" style={{ padding: '0px' }}>
                    <CloseSidebar>
                      <button
                        type="button"
                        data-bs-dismiss="offcanvas"
                        aria-label="Close"
                        ref={closeRef}
                      />
                    </CloseSidebar>

                    <InfoMenu brandInfo={brandInfo} menuInfo={menuInfo} />
                    <MainMenu
                      hideSidebar={hideSidebar}
                      isSettlement={menuInfo.isSettlement}
                      isNewNotice={noticeAlert}
                    />
                    <FooterMenu
                      hiddenYn={hiddenYn}
                      hideSidebar={hideSidebar}
                      onClickSignOut={onClickSignOut}
                    />
                  </div>
                </div>
              </div>
            </ThemeProvider>
          </StoreContext.Provider>
        </>
      )}
    </>
  )
}

export default App

const SideBarWrapper = styled.div`
  width: 240px;
  display: flex;
  flex-direction: column;
  padding: 0;
  background-color: ${colors.W2};

  @media ${(props) => props.theme.tablet} {
    display: none;
  }

  /* @media (max-width: 992px) {
    display: none;
  } */
`

const SideBarWrapperMo = styled.div`
  display: none;
  flex-direction: column;
  padding: 0;
  background-color: ${colors.W2};

  @media ${(props) => props.theme.tablet} {
    display: flex;

    div.offcanvas.offcanvas-start {
      width: 241px;
    }

    div.offcanvas-body {
      position: relative;
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  @media ${(props) => props.theme.mobile} {
    display: flex;

    div.offcanvas.offcanvas-start {
      width: 361px;
    }

    div.offcanvas-body {
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
`

const HeaderWrapperMo = styled.div`
  display: none;
  justify-content: center;
  margin-bottom: 26px;

  @media ${(props) => props.theme.tablet} {
    display: flex;
  }

  @media ${(props) => props.theme.mobile} {
    margin-bottom: 40px;
  }
`

const CurrentPageHeader = styled.div`
  margin-top: 5px;
  font-weight: 700;
  color: ${colors.BK};
`

const OpenSidebar = styled.div`
  display: none;
  position: absolute;
  left: 20px;

  button {
    width: 20px;
    height: 20px;
    background: url(${OpenImg});
    background-repeat: no-repeat;
  }

  @media ${(props) => props.theme.tablet} {
    display: flex;
  }
`

const CloseSidebar = styled.div`
  display: flex;
  position: absolute;
  left: 20px;
  top: 20px;

  button {
    width: 20px;
    height: 20px;
    background: url(${CloseImg});
    background-repeat: no-repeat;
  }
`

const ContentsWrapper = styled.div`
  flex: 1;
  min-width: 360px;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 30px 20px 0;

  @media ${(props) => props.theme.tablet} {
    padding: 25px 20px 0;
  }

  @media ${(props) => props.theme.mobile} {
    padding: 25px 0 0;
  }
`

// const TestBtn = styled.div`
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   position: fixed;
//   width: 60px;
//   height: 40px;
//   right: 1%;
//   top: 30%;
//   background-color: ${colors.mainG};
//   color: ${colors.W1};
//   border-radius: 8px;
//   cursor: pointer;
//   z-index: 9999;
// `
